import { inject, PLATFORM_ID } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot } from '@angular/router';
import { isBrowser } from '../utils/platform.util';
import { PermissionService } from '../permissions/permission.service';
import { AuthService } from '../auth/auth.service';

/**
 * Functional guard for route protection based on permissions
 * 
 * Reads required permission from route.data['permission'] or route.data['permissions']
 * Supports both single BigInt and array of BigInt (OR logic)
 * 
 * Usage in routes:
 * {
 *   path: 'admin',
 *   component: AdminComponent,
 *   canActivate: [permissionGuard],
 *   data: { permission: PERMISSIONS.ADMIN_SYSTEM }
 * }
 * 
 * Or with multiple permissions (OR logic):
 * {
 *   path: 'editor',
 *   component: EditorComponent,
 *   canActivate: [permissionGuard],
 *   data: { permissions: [PERMISSIONS.ADDING_CONTENT_IN_NEWS_SECTION, PERMISSIONS.EDITING_CONTENT_IN_NEWS_SECTION] }
 * }
 */
export const permissionGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const platformId = inject(PLATFORM_ID);
  const permissionService = inject(PermissionService);
  const router = inject(Router);
  const authService = inject(AuthService);

  if (!isBrowser(platformId)) {
    return true;
  }

  // Get required permission(s) from route data
  // Support both 'permission' (single) and 'permissions' (array) keys
  const requiredPermission = route.data['permission'] as bigint | undefined;
  const requiredPermissions = route.data['permissions'] as bigint[] | undefined;

  // If no permissions are required, allow access
  if (!requiredPermission && !requiredPermissions) {
    return true;
  }

  // Check if user is authenticated first
  if (!authService.isAuthenticated()) {
    return router.createUrlTree(['/login']);
  }

  // Force Change Password if required
  // Skip if already on change-password route to avoid infinite loop
  if (authService.mustChangePassword() && route.routeConfig?.path !== 'change-password') {
    return router.createUrlTree(['/change-password']);
  }

  // Check permissions
  let hasPermission = false;

  if (requiredPermission !== undefined) {
    // Single permission check
    hasPermission = permissionService.hasPermission(requiredPermission);
  } else if (requiredPermissions !== undefined && requiredPermissions.length > 0) {
    // Multiple permissions (OR logic)
    hasPermission = permissionService.hasAnyPermission(...requiredPermissions);
  }

  if (!hasPermission) {
    // Redirect to 403 or login page
    return router.createUrlTree(['**']);
  }

  return true;
};

